/**
 * Paths — dirt walkways connecting zones.
 *
 * Implemented as flat ribbons (ShapeGeometry) draped along the terrain at
 * the ground height, with a warm dirt color. Each segment follows the
 * terrain's heightfield so it visually sits on the ground without z-fighting.
 */

import * as THREE from 'three';
import type { Terrain } from '@/world/Terrain';

export interface PathPoint { x: number; z: number; width?: number }

export class Paths {
  static build(terrain: Terrain, routes: PathPoint[][]): THREE.Group {
    const group = new THREE.Group();
    group.name = 'Paths';

    const mat = new THREE.MeshStandardMaterial({
      color: 0x8a6338,
      roughness: 0.95,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1
    });

    for (const route of routes) {
      if (route.length < 2) continue;
      // Subdivide each segment so the ribbon drapes along the terrain.
      const dense: { x: number; z: number; width: number }[] = [];
      for (let i = 0; i < route.length - 1; i++) {
        const a = route[i];
        const b = route[i + 1];
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const segLen = Math.hypot(dx, dz);
        const steps = Math.max(4, Math.ceil(segLen / 3));
        for (let s = 0; s < steps; s++) {
          const t = s / steps;
          dense.push({
            x: a.x + dx * t,
            z: a.z + dz * t,
            width: (a.width ?? 4) * (1 - t) + (b.width ?? a.width ?? 4) * t
          });
        }
      }
      dense.push({
        x: route[route.length - 1].x,
        z: route[route.length - 1].z,
        width: route[route.length - 1].width ?? 4
      });

      // Build a strip geometry from the dense points
      const vertices: number[] = [];
      const uvs: number[] = [];
      for (let i = 0; i < dense.length; i++) {
        const p = dense[i];
        // Compute perpendicular direction using neighbors
        const prev = dense[Math.max(0, i - 1)];
        const next = dense[Math.min(dense.length - 1, i + 1)];
        const tx = next.x - prev.x;
        const tz = next.z - prev.z;
        const len = Math.hypot(tx, tz) || 1;
        const nx = -tz / len;
        const nz = tx / len;
        const half = p.width / 2;
        const y = terrain.heightAt(p.x, p.z) + 0.02;
        vertices.push(p.x + nx * half, y, p.z + nz * half);
        vertices.push(p.x - nx * half, y, p.z - nz * half);
        uvs.push(0, i / (dense.length - 1));
        uvs.push(1, i / (dense.length - 1));
      }
      const indices: number[] = [];
      for (let i = 0; i < dense.length - 1; i++) {
        const a = i * 2;
        indices.push(a, a + 1, a + 2);
        indices.push(a + 1, a + 3, a + 2);
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
      geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
      geo.setIndex(indices);
      geo.computeVertexNormals();
      const mesh = new THREE.Mesh(geo, mat);
      mesh.receiveShadow = true;
      group.add(mesh);
    }

    return group;
  }
}
